import ReactSelect from "react-select";
import CreatableSelect from "react-select/creatable";
import type {
  ClearIndicatorProps,
  DropdownIndicatorProps,
  MultiValueRemoveProps,
  OptionProps,
  Props as ReactSelectProps,
} from "react-select";
import { Check, ChevronDown, X } from "../icons";
import cn from "../../utils/cn";
import type { Option } from "../../types/select";
import { createElement, isValidElement, useCallback, useRef } from "react";
import { getIconComponent } from "../../utils/icons";

function DropdownIndicator<T>(_props: DropdownIndicatorProps<T>): JSX.Element {
  const { className, ...rest } = _props.innerProps;
  const { isDisabled } = _props.selectProps;

  return (
    <div
      className={cn(
        "grid place-items-center text-gray-500",
        isDisabled && "text-gray-300",
        className
      )}
      {...rest}
      aria-label="Open"
    >
      <ChevronDown
        className={cn(
          "ease-[cubic-bezier(0.87,_0,_0.13,_1)] transition-transform duration-300",
          _props.selectProps.menuIsOpen && "rotate-180"
        )}
        size={20}
      />
    </div>
  );
}

function ClearIndicator<T>(_props: ClearIndicatorProps<T>): JSX.Element {
  const { className, ...rest } = _props.innerProps;
  return (
    <div
      className={cn("grid place-items-center", className)}
      {...rest}
      aria-label="Clear"
    >
      <X className={cn("text-gray-500 cursor-pointer")} size={18} />
    </div>
  );
}

function OptionComp<T>(props: OptionProps<T>): JSX.Element {
  const { children, className, isSelected, innerRef, innerProps } = props;
  return (
    <div
      {...innerProps}
      className={cn(
        "hover:bg-gray-50 py-2.5 px-[14px] text-xs md:text-sm text-gray-900 font-medium break-words",
        className
      )}
      ref={innerRef}
    >
      <div className="flex gap-2 justify-between items-center w-full">
        <div className="text-wrap">{children}</div>
        {isSelected ? (
          <Check className="text-primary-600 shrink-0" size={20} />
        ) : null}
      </div>
    </div>
  );
}

function MultiValueRemove<T>(props: MultiValueRemoveProps<T>): JSX.Element {
  const { className, ...rest } = props.innerProps;
  const { isDisabled } = props.selectProps;
  return (
    <div
      className={cn(
        "grid place-items-center text-gray-700 cursor-pointer",
        isDisabled && "hidden",
        className
      )}
      {...rest}
      aria-label="Remove"
    >
      <X size={12} />
    </div>
  );
}

function defaultFormatOptionLabel(data: any) {
  const { Icon } = data as Option;
  let iconNode: React.ReactNode = null;
  if (Icon) {
    if (typeof Icon === "string") {
      const Comp = getIconComponent(Icon as any);
      iconNode = Comp ? createElement(Comp) : null;
    } else if (isValidElement(Icon)) {
      iconNode = Icon;
    } else {
      const Comp = Icon as React.ElementType;
      iconNode = Comp ? createElement(Comp) : null;
    }
  }

  return (
    <div className={cn("flex gap-2 items-center [&>svg]:size-4")}>
      {iconNode}
      <span>{data.label}</span>
    </div>
  );
}

type SelectProps = ReactSelectProps & {
  isCreatable?: boolean;
  formatCreateLabel?: (inputValue: string) => string;
  onCreateOption?: (inputValue: string) => void;
  options: Option[];
};

function defaultFilterOption(
  option: { label: string; value: string; data: any },
  inputValue: string
): boolean {
  const search = inputValue.trim().toLowerCase();
  const label = String(option.label ?? "").toLowerCase();
  if (label.includes(search)) return true;
  const value = String(option.value ?? "").toLowerCase();
  return value.includes(search);
}

function Select({
  className,
  "aria-invalid": ariaInvalid,
  options,
  formatOptionLabel = defaultFormatOptionLabel,
  filterOption = defaultFilterOption,
  onKeyDown,
  onInputChange,
  ...props
}: SelectProps): JSX.Element {
  const Comp = props.isCreatable ? CreatableSelect : ReactSelect;
  const inputValueRef = useRef("");

  const handleInputChange = useCallback(
    (value: string, meta: any) => {
      inputValueRef.current = value;
      onInputChange?.(value, meta);
    },
    [onInputChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Prevent react-select from selecting the focused option on space key.
      // By default, react-select treats space as "select" when input is empty.
      // We only intercept when there's no input yet to allow mid-word spaces.
      if (e.key === " " && !inputValueRef.current) {
        e.preventDefault();
      }
      onKeyDown?.(e);
    },
    [onKeyDown]
  );

  return (
    <Comp
      options={options}
      onCreateOption={props.onCreateOption}
      formatCreateLabel={props.formatCreateLabel}
      aria-invalid={ariaInvalid}
      filterOption={filterOption}
      onKeyDown={handleKeyDown}
      onInputChange={handleInputChange}
      className={cn(className)}
      classNames={{
        control: (state) =>
          cn(
            "bg-white py-2.5 px-[14px] rounded-lg border border-gray-300 shadow-xs hover:border-gray-400 text-xs md:text-sm",
            {
              "border-gray-300 bg-gray-50": state.isDisabled,
              "border-indigo-300 hover:border-indigo-300 ring-[4px] ring-indigo-100":
                state.isFocused,
              "border-error-500": ariaInvalid,
            }
          ),
        input: () => "text-gray-900",
        singleValue: (state) =>
          cn(" [&_span]:truncate", {
            "text-gray-500": state.isDisabled,
          }),
        menu: (state) =>
          cn(
            "overflow-hidden bg-white border border-gray-100 rounded-lg shadow-lg",
            {
              "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200":
                state.selectProps.menuIsOpen,
              "animate-out fade-out-0 zoom-out-95 slide-out-to-top-2 duration-200":
                !state.selectProps.menuIsOpen,
            }
          ),
        placeholder: (state) =>
          cn("text-gray-500 text-start truncate", {
            "text-gray-300": state.isDisabled,
          }),
        noOptionsMessage: () => "py-2.5 px-[14px] text-gray-500",
        multiValue: (state) =>
          cn("rounded-[4px] py-0 px-[5px] bg-gray-50 flex items-center gap-1", {
            "bg-gray-100": state.isDisabled,
          }),
        multiValueLabel: (state) =>
          cn("text-gray-700 font-medium", {
            "text-gray-400": state.isDisabled,
          }),
        valueContainer: () => "flex flex-wrap gap-[5px]",
        indicatorsContainer: () => "flex items-center gap-0.5",
      }}
      components={{
        DropdownIndicator,
        MultiValueRemove,
        ClearIndicator,
        Option: OptionComp,
      }}
      data-slot="select"
      menuPortalTarget={
        typeof document !== "undefined" ? document.body : undefined
      }
      menuPosition="fixed"
      styles={{
        menu: (base) => ({
          ...base,
          zIndex: 9999,
        }),
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
      unstyled
      formatOptionLabel={formatOptionLabel}
      {...props}
    />
  );
}

export { Select };
